import NoImage from 'assets/images/noimage.png'
import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ChevronDown, Plus, Trash2 } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { URL_CONFIGURATIONS, URL_WORKPLACE } from 'utils/pathUrl'
import ModalAddProcess from './ModalAddProcess'
import {
  useFetchFindoraProductProcessListQuery,
  useRemoveProcessMutation,
} from './productMappingListApiSlice'
import { setFetchOption } from './productMappingListSlice'

const ListProductMapping = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((state) => state.productMappingList)

  const isMobile = useIsMobile()

  const [searchInput, setSearchInput] = useState('')
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [imageItems, setImagesItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [isOpenModalAddProcess, setIsOpenModalAddProcess] = useState(false)
  const [dataProcessById, setDataProcessById] = useState(null)

  const [removeProcess, { isLoading: isRemove }] = useRemoveProcessMutation()

  const { data, isLoading, refetch, isFetching } =
    useFetchFindoraProductProcessListQuery(
      {
        page: tag?.page ?? 0,
        size: tag?.size ?? 10,
        s: tag?.s,
        sortby: tag?.sortby,
        sortdir: tag?.sortdir,
      },
      { refetchOnMountOrArgChange: true, skip: false },
    )

  const onHandleOpenImageModal = (imageUrls) => {
    setImagesItems([imageUrls])
    setModalOpenImage(true)
  }

  // NEW COLUMNS
  const columns = [
    {
      name: <span>Nama Proses</span>,
      selector: 'product_process_name',
      grow: 2,
      // sortable: true,
      // sortField: 'product_process_name',
      cell: (row) => {
        return <span className="m-0">{row?.product_process_name}</span>
      },
    },
    {
      name: <span>Nama Barang</span>,
      selector: 'item.nameItem',
      grow: 2,
      cell: (row, index) => {
        return (
          <div className="flex-centered gap-2">
            <img
              src={
                row?.item?.linkFoto && row?.item?.linkFoto.length > 0
                  ? row?.item?.linkFoto
                  : NoImage
              }
              alt={row?.item?.nameItem || `barang ${index + 1}`}
              width={32}
              height={32}
              className="rounded-circle"
              onClick={() =>
                onHandleOpenImageModal(row?.item?.linkFoto || NoImage)
              }
              style={{ cursor: 'pointer' }}
            />
            <span className="m-0">{row?.item?.nameItem}</span>
          </div>
        )
      },
    },
    {
      name: <span>SKU</span>,
      selector: 'SKU',
      grow: 2,
      cell: (row) => {
        return <span>{row?.item?.SKU ?? '-'}</span>
      },
    },
    {
      name: '',
      width: '150px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: '16px', flexGrow: 1 }}
            data-testid={`product-mapping-table-row-${row?.id}`}
          >
            <div id={`remove-${row?.id}`}>
              <Trash2
                color="red"
                size={18}
                onClick={() => {
                  setIsOpenDeleteModal(true)
                  setDataProcessById(row)
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
          </div>
        )
      },
    },
  ]

  // HANDLE SUBMIT REMOVE BY PROCESS ID
  const handleSubmitRemoveById = async (id) => {
    try {
      if (!id) {
        enqueueSnackbar('Tidak ada data yang dipilih untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const response = await removeProcess(id)
      if (response && response?.data) {
        enqueueSnackbar(
          `Barang ${dataProcessById?.item?.nameItem} berhasil dihapus pada proses ${dataProcessById?.product_process_name}`,
          {
            variant: 'success',
          },
        )
        setIsOpenDeleteModal(false)
        setDataProcessById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Proses gagal dihapus', {
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('refetch') === 'true') {
      refetch()
      navigate(URL_CONFIGURATIONS)
    }
  }, [location.search])

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />

      {/* MODAL ADD PROCESS */}
      <ModalAddProcess
        isOpen={isOpenModalAddProcess}
        toggle={() => {
          setIsOpenModalAddProcess((prev) => !prev)
          setDataProcessById(null)
        }}
        onSuccess={refetch}
        dataExistingConfiguration={data || []}
        dataProcessById={dataProcessById}
      />

      {/* MODAL REMOVE CONFIGURATION */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal((prev) => !prev)
          setDataProcessById(null)
        }}
        title={`Hapus Proses`}
        dataId={dataProcessById?.id}
        desc={`Apakah Anda yakin ingin menghapus barang ${dataProcessById?.item?.nameItem} pada proses ${dataProcessById?.product_process_name}?`}
        isLoading={isRemove}
        onRemoveData={(id) => handleSubmitRemoveById(id)}
        buttonTitle="Hapus"
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Proses Pemetaan Barang"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={true}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={tag?.page}
                rowsPerPage={tag?.size}
                setCurrentPage={(e) => {
                  dispatch(setFetchOption({ page: e }))
                }}
                setRowsPerPage={(e) => {
                  dispatch(setFetchOption({ size: e }))
                }}
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">
                    Daftar Proses Pemetaan Barang
                  </h5>

                  <div className="d-flex gap-2">
                    <Button
                      onClick={() => setIsOpenModalAddProcess(true)}
                      size="sm"
                      color="primary"
                      className="d-flex align-items-center gap-2"
                    >
                      <Plus size={16} />
                      Tambah
                    </Button>
                  </div>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label="Cari"
                    value={searchInput}
                    placeholder="Cari nama proses..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth={isMobile ? '100%' : '15%'}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada daftar proses pemetaan barang yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListProductMapping
